import {useQuery} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'
import useRequestParams from 'src/feature/bite/hook/request'

import {ReferralProgramSchemaResponse, ReferralCampaignProductListSchemaResponse} from '../domain'
import {useReferral} from '../hook'
import {toast} from 'react-toastify'
import {useSearchParams} from 'react-router-dom'
import {CAMPAIGN_API} from 'src/feature/bite/domain'

const apiUrlRedeem = `${CAMPAIGN_API}/Campaign`
const apiUrlProgram = `${CAMPAIGN_API}/ReferralProgram`
const apiUrlProduct = `${CAMPAIGN_API}/Product`
const apiCampaignProduct = `${CAMPAIGN_API}/CampaignProduct`
const apiCampaignFile = `${CAMPAIGN_API}/CampaignFile`

export function useGetProgramDetail() {
  const auth = useAuthUser()
  const {headers, errorMessage, signOut} = useRequestParams()
  const {programSet} = useReferral()

  const userID = auth()?.id

  return useQuery(
    ['referralProgram'],
    async () => {
      const Response = await fetch(`${apiUrlProgram}/GetReferralProgram`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          // storeID: '996225251718',
        }),
      })
      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const safeParse = ReferralProgramSchemaResponse.safeParse(Data)
      const parse = ReferralProgramSchemaResponse.parse(Data)
      if (parse.result.isSuccess === false) {
        toast.error(parse.result.errorMessage || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      programSet(parse.referralProgramData)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCampaignProducts({campaignId}: {campaignId: string}) {
  const auth = useAuthUser()
  const {
    referralCampaignProductList,
    referralCampaignProductListSet,
    selectedCampaignProduct,
    selectedCampaignProductSet,
  } = useReferral()
  const {headers} = useRequestParams()
  const userID = auth()?.id

  return useQuery(
    [
      'campaignProducts',
      campaignId,
      // referralCampaignProductList.paging.currentPageNumber,
      // referralCampaignProductList.paging.pageSize,
    ],
    async () => {
      if (!campaignId || !userID || campaignId === '0') return null

      const response = await fetch(apiCampaignProduct + '/GetAllCampaignProduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.accessToken}`,
        },
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          campaignId,
          filterList: [],
          hasProductOption: false,
          orderList: [],
          type: 1,
          paging: referralCampaignProductList.paging,
        }),
      })
      const data = await response.json()

      const safeParse = ReferralCampaignProductListSchemaResponse.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ReferralCampaignProductListSchemaResponse.parse(data)

      referralCampaignProductListSet(parse)
      selectedCampaignProductSet(parse.data)

      return parse.data
    },
    {
      enabled: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: true,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: () => {},
      onError: (error) => {},
    }
  )
}

export function useGetCampaignProductsAll({campaignId}: {campaignId: string}) {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const {referralCampaignProductListAll, referralCampaignProductListAllSet} = useReferral()

  const userID = auth()?.id

  return useQuery(
    [
      'campaignProductsAll',
      referralCampaignProductListAll.paging.currentPageNumber,
      referralCampaignProductListAll.paging.pageSize,
    ],
    async () => {
      if (!userID) return null

      const response = await fetch(apiUrlProduct + '/GetAllProduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.accessToken}`,
        },
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          filterList: [],
          campaignID: '',
          hasProductOption: false,
          orderList: [],
          type: 1,
          paging: referralCampaignProductListAll.paging,
        }),
      })
      const data = await response.json()

      const safeParse = ReferralCampaignProductListSchemaResponse.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ReferralCampaignProductListSchemaResponse.parse(data)

      referralCampaignProductListAllSet(parse)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: false,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: () => {},
      onError: (error) => {},
    }
  )
}

export function useGetRewardDetail() {
  const {headers, errorMessage, signOut} = useRequestParams()
  const [params] = useSearchParams()
  const auth = useAuthUser()

  const userID = auth()?.id
  const campaignID = params.get('campaignId')

  return useQuery(
    ['referralReward', campaignID],
    async () => {
      if (!campaignID || !userID || campaignID === '0') return null

      const Response = await fetch(`${apiUrlRedeem}/GetCampaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          campaignID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      if (Data.campaign === null) {
        toast.error('Campaign not found, please contact support', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      }

      return Data
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCampaignFiles({campaignId}: {campaignId: string}) {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const {campaignFilesSet, uploadProductsSet} = useReferral()

  const userID = auth()?.id

  return useQuery(
    ['campaignFiles', campaignId],
    async () => {
      if (!campaignId || !userID || campaignId === '0') return null

      const response = await fetch(apiCampaignFile + '/GetAllCampaignFile', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          campaignId,
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })
      const data = await response.json()

      const fileIDList = data.data.map((file) => file.id)
      const firstFile = data.data[0]

      const ResponseProduct = await fetch(`${apiCampaignProduct}/GetAllCampaignProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth().id,
          language: headers.Language,
          campaignID: '',
          fileIDList,
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      const DataProduct = await ResponseProduct.json()

      const productsAddType = DataProduct.data.map((product) => {
        return {
          ...product,
          type: firstFile?.type || 1,
        }
      })

      campaignFilesSet(data.data)
      uploadProductsSet(productsAddType || [])

      return data
    },
    {
      enabled: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: true,
      cacheTime: 0,
      staleTime: 0,
      onSuccess: () => {},
      onError: (error) => {},
    }
  )
}
